import React from "react";
import {
  Accordion,
  Field,
  TextArea,
  Toggle,
  ImageField,
  ButtonListEditor,
} from "./editorControls";

export function HomepageEditor({ value, onChange, onStatus }) {
  const v = value || {};
  const block = (key) => v[key] || {};
  const setBlock = (key, patch) => onChange({ ...v, [key]: { ...(v[key] || {}), ...patch } });

  return (
    <section className="admin-section admin-section-wide" data-testid="homepage-editor">
      <h2>Homepage</h2>
      <p className="admin-hint">
        Edit every section of the homepage. Toggle a section off to hide it without deleting anything.
        Use <strong>Save Draft</strong> to store changes privately, <strong>Preview</strong> to see them,
        then <strong>Publish</strong> to go live.
      </p>

      {/* HERO */}
      <Accordion title="Hero Banner" subtitle="Top of the page" defaultOpen>
        <Toggle label="Show hero section" checked={block("hero").visible !== false} onChange={(c) => setBlock("hero", { visible: c })} testid="hp-hero-visible" />
        <Field label="Eyebrow (small line above headline)" value={block("hero").eyebrow} onChange={(x) => setBlock("hero", { eyebrow: x })} testid="hp-hero-eyebrow" />
        <Field label="Headline" value={block("hero").title} onChange={(x) => setBlock("hero", { title: x })} testid="hp-hero-title" />
        <TextArea label="Supporting text" value={block("hero").copy} onChange={(x) => setBlock("hero", { copy: x })} testid="hp-hero-copy" />
        <StringList title="Highlight facts (right panel)" items={block("hero").facts || []} onChange={(f) => setBlock("hero", { facts: f })} prefix="hp-hero-fact" />
        <ImageField label="Hero background image" image={block("hero").image} onChange={(img) => setBlock("hero", { image: img })} onStatus={onStatus} testid="hp-hero-image" />
        <ButtonListEditor buttons={block("hero").buttons} onChange={(b) => setBlock("hero", { buttons: b })} prefix="hp-hero-btn" />
      </Accordion>

      {/* QUICK ACTIONS */}
      <Accordion title="Quick Actions" subtitle="The four shortcut cards">
        <Toggle label="Show quick actions" checked={block("quickActions").visible !== false} onChange={(c) => setBlock("quickActions", { visible: c })} testid="hp-qa-visible" />
        <QuickActionsList items={block("quickActions").items || []} onChange={(items) => setBlock("quickActions", { items })} />
      </Accordion>

      {/* VIDEO */}
      <Accordion title="Video Feature" subtitle="Course preview embed">
        <Toggle label="Show video section" checked={block("video").visible !== false} onChange={(c) => setBlock("video", { visible: c })} testid="hp-video-visible" />
        <Field label="Eyebrow" value={block("video").eyebrow} onChange={(x) => setBlock("video", { eyebrow: x })} testid="hp-video-eyebrow" />
        <Field label="Heading" value={block("video").title} onChange={(x) => setBlock("video", { title: x })} testid="hp-video-title" />
        <TextArea label="Text" value={block("video").copy} onChange={(x) => setBlock("video", { copy: x })} testid="hp-video-copy" />
        <Field label="YouTube embed URL" value={block("video").embedUrl} onChange={(x) => setBlock("video", { embedUrl: x })} testid="hp-video-url" placeholder="https://www.youtube-nocookie.com/embed/..." />
      </Accordion>

      {/* ABOUT */}
      <Accordion title="About Preview" subtitle="Photo + intro block">
        <Toggle label="Show about section" checked={block("about").visible !== false} onChange={(c) => setBlock("about", { visible: c })} testid="hp-about-visible" />
        <Field label="Eyebrow" value={block("about").eyebrow} onChange={(x) => setBlock("about", { eyebrow: x })} testid="hp-about-eyebrow" />
        <Field label="Heading" value={block("about").title} onChange={(x) => setBlock("about", { title: x })} testid="hp-about-title" />
        <TextArea label="Text" value={block("about").copy} onChange={(x) => setBlock("about", { copy: x })} testid="hp-about-copy" />
        <Field label="Photo caption" value={block("about").photoLabel} onChange={(x) => setBlock("about", { photoLabel: x })} testid="hp-about-caption" />
        <Field label="Button label" value={block("about").buttonLabel} onChange={(x) => setBlock("about", { buttonLabel: x })} testid="hp-about-btn-label" />
        <Field label="Button link" value={block("about").buttonHref} onChange={(x) => setBlock("about", { buttonHref: x })} testid="hp-about-btn-href" />
        <p className="admin-hint">The four feature chips are pulled from the “About Page” tab’s Club Features.</p>
        <ImageField label="About photo" image={block("about").image} onChange={(img) => setBlock("about", { image: img })} onStatus={onStatus} testid="hp-about-image" />
      </Accordion>

      {/* RATES */}
      <Accordion title="Rates Preview" subtitle="Heading + button (prices come from Rates tab)">
        <Toggle label="Show rates section" checked={block("rates").visible !== false} onChange={(c) => setBlock("rates", { visible: c })} testid="hp-rates-visible" />
        <Field label="Eyebrow" value={block("rates").eyebrow} onChange={(x) => setBlock("rates", { eyebrow: x })} testid="hp-rates-eyebrow" />
        <Field label="Heading" value={block("rates").title} onChange={(x) => setBlock("rates", { title: x })} testid="hp-rates-title" />
        <TextArea label="Text" value={block("rates").copy} onChange={(x) => setBlock("rates", { copy: x })} testid="hp-rates-copy" />
        <Field label="Button label" value={block("rates").buttonLabel} onChange={(x) => setBlock("rates", { buttonLabel: x })} testid="hp-rates-btn-label" />
        <Field label="Button link" value={block("rates").buttonHref} onChange={(x) => setBlock("rates", { buttonHref: x })} testid="hp-rates-btn-href" />
      </Accordion>

      {/* MEMBERSHIP */}
      <Accordion title="Membership Preview" subtitle="Heading + featured plan card">
        <Toggle label="Show membership section" checked={block("membership").visible !== false} onChange={(c) => setBlock("membership", { visible: c })} testid="hp-mem-visible" />
        <Field label="Eyebrow" value={block("membership").eyebrow} onChange={(x) => setBlock("membership", { eyebrow: x })} testid="hp-mem-eyebrow" />
        <Field label="Heading" value={block("membership").title} onChange={(x) => setBlock("membership", { title: x })} testid="hp-mem-title" />
        <TextArea label="Text" value={block("membership").copy} onChange={(x) => setBlock("membership", { copy: x })} testid="hp-mem-copy" />
        <Field label="Button label" value={block("membership").buttonLabel} onChange={(x) => setBlock("membership", { buttonLabel: x })} testid="hp-mem-btn-label" />
        <Field label="Button link" value={block("membership").buttonHref} onChange={(x) => setBlock("membership", { buttonHref: x })} testid="hp-mem-btn-href" />
        <ImageField label="Card image" image={block("membership").image} onChange={(img) => setBlock("membership", { image: img })} onStatus={onStatus} testid="hp-mem-image" />
      </Accordion>

      {/* EVENTS HEADING */}
      <Accordion title="Events Section Heading" subtitle="The event cards are managed in the Events tab">
        <Toggle label="Show events section" checked={block("events").visible !== false} onChange={(c) => setBlock("events", { visible: c })} testid="hp-events-visible" />
        <Field label="Eyebrow" value={block("events").eyebrow} onChange={(x) => setBlock("events", { eyebrow: x })} testid="hp-events-eyebrow" />
        <Field label="Heading" value={block("events").title} onChange={(x) => setBlock("events", { title: x })} testid="hp-events-title" />
        <TextArea label="Text" value={block("events").copy} onChange={(x) => setBlock("events", { copy: x })} testid="hp-events-copy" />
      </Accordion>

      {/* JUNIOR */}
      <Accordion title="Junior / Families" subtitle="Junior golf + camp block">
        <Toggle label="Show junior section" checked={block("junior").visible !== false} onChange={(c) => setBlock("junior", { visible: c })} testid="hp-junior-visible" />
        <Field label="Eyebrow" value={block("junior").eyebrow} onChange={(x) => setBlock("junior", { eyebrow: x })} testid="hp-junior-eyebrow" />
        <Field label="Heading" value={block("junior").title} onChange={(x) => setBlock("junior", { title: x })} testid="hp-junior-title" />
        <TextArea label="Text" value={block("junior").copy} onChange={(x) => setBlock("junior", { copy: x })} testid="hp-junior-copy" />
        <div className="admin-row">
          <input value={block("junior").exploreLabel || ""} onChange={(e) => setBlock("junior", { exploreLabel: e.target.value })} placeholder="Button 1 label" data-testid="hp-junior-explore-label" />
          <input value={block("junior").exploreHref || ""} onChange={(e) => setBlock("junior", { exploreHref: e.target.value })} placeholder="Button 1 link" data-testid="hp-junior-explore-href" />
        </div>
        <div className="admin-row">
          <input value={block("junior").campLabel || ""} onChange={(e) => setBlock("junior", { campLabel: e.target.value })} placeholder="Button 2 label" data-testid="hp-junior-camp-label" />
          <input value={block("junior").campHref || ""} onChange={(e) => setBlock("junior", { campHref: e.target.value })} placeholder="Button 2 link" data-testid="hp-junior-camp-href" />
        </div>
        <Field label="Card title" value={block("junior").cardTitle} onChange={(x) => setBlock("junior", { cardTitle: x })} testid="hp-junior-card-title" />
        <Field label="Card line 1" value={block("junior").cardLine1} onChange={(x) => setBlock("junior", { cardLine1: x })} testid="hp-junior-card-line1" />
        <Field label="Card line 2" value={block("junior").cardLine2} onChange={(x) => setBlock("junior", { cardLine2: x })} testid="hp-junior-card-line2" />
        <ImageField label="Card image" image={block("junior").image} onChange={(img) => setBlock("junior", { image: img })} onStatus={onStatus} testid="hp-junior-image" />
      </Accordion>

      {/* SAND TRAP */}
      <Accordion title="The Sand Trap" subtitle="Restaurant block">
        <Toggle label="Show Sand Trap section" checked={block("sandtrap").visible !== false} onChange={(c) => setBlock("sandtrap", { visible: c })} testid="hp-st-visible" />
        <Field label="Eyebrow" value={block("sandtrap").eyebrow} onChange={(x) => setBlock("sandtrap", { eyebrow: x })} testid="hp-st-eyebrow" />
        <Field label="Heading" value={block("sandtrap").title} onChange={(x) => setBlock("sandtrap", { title: x })} testid="hp-st-title" />
        <TextArea label="Text" value={block("sandtrap").copy} onChange={(x) => setBlock("sandtrap", { copy: x })} testid="hp-st-copy" />
        <Field label="Button label" value={block("sandtrap").buttonLabel} onChange={(x) => setBlock("sandtrap", { buttonLabel: x })} testid="hp-st-btn-label" />
        <Field label="Button link" value={block("sandtrap").buttonHref} onChange={(x) => setBlock("sandtrap", { buttonHref: x })} testid="hp-st-btn-href" />
        <ImageField label="Photo" image={block("sandtrap").image} onChange={(img) => setBlock("sandtrap", { image: img })} onStatus={onStatus} testid="hp-st-image" />
      </Accordion>

      {/* LIFE AT REDLAND */}
      <Accordion title="Life at Redland" subtitle="Featured course image strip">
        <Toggle label="Show image strip" checked={block("life").visible !== false} onChange={(c) => setBlock("life", { visible: c })} testid="hp-life-visible" />
        <Field label="Eyebrow" value={block("life").eyebrow} onChange={(x) => setBlock("life", { eyebrow: x })} testid="hp-life-eyebrow" />
        <Field label="Heading" value={block("life").title} onChange={(x) => setBlock("life", { title: x })} testid="hp-life-title" />
        <ImageStrip images={block("life").images || []} onChange={(images) => setBlock("life", { images })} onStatus={onStatus} />
      </Accordion>

      {/* FINAL CTA */}
      <Accordion title="Final Call-to-Action" subtitle="Bottom tee-times banner">
        <Toggle label="Show final CTA" checked={block("finalCta").visible !== false} onChange={(c) => setBlock("finalCta", { visible: c })} testid="hp-cta-visible" />
        <Field label="Eyebrow" value={block("finalCta").eyebrow} onChange={(x) => setBlock("finalCta", { eyebrow: x })} testid="hp-cta-eyebrow" />
        <Field label="Heading" value={block("finalCta").title} onChange={(x) => setBlock("finalCta", { title: x })} testid="hp-cta-title" />
        <TextArea label="Note text" value={block("finalCta").note} onChange={(x) => setBlock("finalCta", { note: x })} testid="hp-cta-note" />
        <ImageField label="Background image" image={block("finalCta").image} onChange={(img) => setBlock("finalCta", { image: img })} onStatus={onStatus} testid="hp-cta-image" />
        <ButtonListEditor buttons={block("finalCta").buttons} onChange={(b) => setBlock("finalCta", { buttons: b })} prefix="hp-cta-btn" />
      </Accordion>
    </section>
  );
}

function StringList({ title, items, onChange, prefix }) {
  return (
    <div className="admin-subblock">
      <h3>{title}</h3>
      {items.map((it, i) => (
        <div className="admin-row" key={i}>
          <input value={it} onChange={(e) => onChange(items.map((x, idx) => (idx === i ? e.target.value : x)))} data-testid={`${prefix}-${i}`} />
          <button className="admin-mini danger" onClick={() => { if (window.confirm("Remove this item?")) onChange(items.filter((_, idx) => idx !== i)); }}>Remove</button>
        </div>
      ))}
      <button className="admin-mini" onClick={() => onChange([...items, ""])} data-testid={`${prefix}-add`}>+ Add</button>
    </div>
  );
}

function QuickActionsList({ items, onChange }) {
  const set = (i, k, v) => onChange(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  return (
    <div className="admin-subblock">
      <h3>Shortcut Cards</h3>
      {items.map((it, i) => (
        <div className="admin-card-editor" key={i}>
          <input value={it.label || ""} onChange={(e) => set(i, "label", e.target.value)} placeholder="Card title" data-testid={`hp-qa-label-${i}`} />
          <input value={it.href || ""} onChange={(e) => set(i, "href", e.target.value)} placeholder="Link" data-testid={`hp-qa-href-${i}`} />
          <textarea rows={2} value={it.detail || ""} onChange={(e) => set(i, "detail", e.target.value)} placeholder="Description" data-testid={`hp-qa-detail-${i}`} />
          <button className="admin-mini danger" onClick={() => { if (window.confirm("Remove this card?")) onChange(items.filter((_, idx) => idx !== i)); }} data-testid={`hp-qa-delete-${i}`}>Remove</button>
        </div>
      ))}
      <button className="admin-mini" onClick={() => onChange([...items, { label: "", href: "/", detail: "" }])} data-testid="hp-qa-add">+ Add Card</button>
    </div>
  );
}

function ImageStrip({ images, onChange, onStatus }) {
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const copy = [...images];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const setItem = (i, patch) => onChange(images.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const upload = async (i, file) => {
    try {
      const { adminFetch } = await import("./auth");
      const fd = new FormData();
      fd.append("file", file);
      const d = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
      setItem(i, { src: d.url });
      onStatus && onStatus("success", "Image uploaded. Save Draft, then Publish.");
    } catch (err) {
      onStatus && onStatus("error", err.message);
    }
  };
  return (
    <div className="admin-subblock">
      <h3>Images</h3>
      {images.map((it, i) => (
        <div className="admin-card-editor" key={i} data-testid={`hp-life-item-${i}`}>
          <div className="admin-hero-preview" style={{ maxWidth: 220 }}>
            {it.src ? <img src={it.src && it.src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${it.src}` : it.src} alt={it.alt || ""} /> : null}
          </div>
          <input value={it.label || ""} onChange={(e) => setItem(i, { label: e.target.value })} placeholder="Caption" data-testid={`hp-life-label-${i}`} />
          <input value={it.alt || ""} onChange={(e) => setItem(i, { alt: e.target.value })} placeholder="Alt text" data-testid={`hp-life-alt-${i}`} />
          <div className="admin-gallery-controls">
            <label className="admin-mini" style={{ cursor: "pointer" }}>
              Replace
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => e.target.files?.[0] && upload(i, e.target.files[0])} data-testid={`hp-life-file-${i}`} />
            </label>
            <button className="admin-mini" onClick={() => move(i, -1)}>↑</button>
            <button className="admin-mini" onClick={() => move(i, 1)}>↓</button>
            <button className="admin-mini danger" onClick={() => { if (window.confirm("Remove this image?")) onChange(images.filter((_, idx) => idx !== i)); }} data-testid={`hp-life-delete-${i}`}>Remove</button>
          </div>
        </div>
      ))}
      <label className="admin-mini" style={{ cursor: "pointer" }} data-testid="hp-life-add">
        + Add Image
        <input type="file" accept="image/*" style={{ display: "none" }} onChange={async (e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          try {
            const { adminFetch } = await import("./auth");
            const fd = new FormData();
            fd.append("file", f);
            const d = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
            onChange([...images, { src: d.url, alt: "", label: "" }]);
            onStatus && onStatus("success", "Image added. Save Draft, then Publish.");
          } catch (err) {
            onStatus && onStatus("error", err.message);
          }
          e.target.value = "";
        }} />
      </label>
    </div>
  );
}
